import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, RotateCcw, Sparkles, Loader2 } from "lucide-react";
import { streamAI } from "@/lib/ai";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { useSite } from "@/lib/site";
import { DEFAULT_TEXTS } from "@/config/landingTexts";

const TextsContext = createContext({ get: (k) => DEFAULT_TEXTS[k] || "", edit: false, open: () => {} });
const VARS = "{prix_fondateur}, {prix_normal}, {date_livraison}, {places}";

function Editor({ k, overrides, onClose, onSaved }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [instruction, setInstruction] = useState("");
  useEffect(() => { if (k) { setValue(overrides[k] ?? DEFAULT_TEXTS[k]); setInstruction(""); } }, [k, overrides]);
  const rewrite = async () => {
    const source = value;
    setAiBusy(true);
    setValue("");
    try { await streamAI("/admin/ai/rewrite", { text: source, instruction }, (d) => setValue((v) => v + d)); }
    catch (e) { toast.error(e.message); setValue(source); }
    finally { setAiBusy(false); }
  };
  const run = async (fn, msg) => {
    setBusy(true);
    try { const { data } = await fn(); onSaved(data); toast.success(msg); onClose(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!k} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[560px] rounded-[14px]" data-testid="text-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Modifier le texte</DialogTitle>
          <DialogDescription>Variables possibles : {VARS}</DialogDescription>
        </DialogHeader>
        <textarea className="field h-auto min-h-[120px] py-2 leading-relaxed" value={value} onChange={(e) => setValue(e.target.value)} disabled={aiBusy} autoFocus data-testid="text-editor-input" />
        <div className="flex flex-col sm:flex-row gap-2">
          <input className="field h-10" placeholder="Consigne pour l'IA (facultatif) : plus court, plus percutant…" value={instruction} onChange={(e) => setInstruction(e.target.value)} disabled={aiBusy} data-testid="text-editor-ai-instruction" />
          <button type="button" className="btn-outline h-10 shrink-0" onClick={rewrite} disabled={aiBusy || !value.trim()} data-testid="text-editor-ai-button">
            {aiBusy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} {aiBusy ? "Reformulation…" : "Reformuler avec l'IA"}
          </button>
        </div>
        {k in overrides && <p className="text-xs text-[#5B665F]" data-testid="text-editor-original">Texte d'origine : « {DEFAULT_TEXTS[k]} »</p>}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2">
          {k in overrides
            ? <button className="btn-ghost text-[#8A4B00]" disabled={busy} onClick={() => run(() => api.delete(`/admin/site-texts/${k}`), "Texte d'origine rétabli")} data-testid="text-editor-reset"><RotateCcw size={15} /> Rétablir le texte d'origine</button>
            : <span />}
          <div className="flex flex-col-reverse sm:flex-row gap-2">
            <button className="btn-outline" onClick={onClose} data-testid="text-editor-cancel">Annuler</button>
            <button className="btn-primary" disabled={busy || aiBusy || !value.trim()} onClick={() => run(() => api.put(`/admin/site-texts/${k}`, { value }), "Texte enregistré")} data-testid="text-editor-save">Enregistrer</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function TextsProvider({ edit, children }) {
  const site = useSite();
  const [overrides, setOverrides] = useState({});
  const [editing, setEditing] = useState(null);
  useEffect(() => { api.get("/site-texts").then((r) => setOverrides(r.data)).catch(() => {}); }, []);
  const get = useCallback((k) => (overrides[k] ?? DEFAULT_TEXTS[k] ?? "")
    .replaceAll("{prix_fondateur}", site.founderPrice).replaceAll("{prix_normal}", site.regularPrice)
    .replaceAll("{date_livraison}", site.deliveryDate).replaceAll("{places}", String(site.seats)), [overrides, site]);
  return (
    <TextsContext.Provider value={{ get, edit, open: setEditing }}>
      {children}
      {edit && <Editor k={editing} overrides={overrides} onClose={() => setEditing(null)} onSaved={setOverrides} />}
    </TextsContext.Provider>
  );
}

export const useTexts = () => useContext(TextsContext);

export function E({ k }) {
  const { get, edit, open } = useTexts();
  if (!edit) return get(k);
  const onClick = (e) => { e.preventDefault(); e.stopPropagation(); open(k); };
  return (
    <span role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => e.key === "Enter" && onClick(e)} title="Cliquer pour modifier"
      className="relative cursor-text rounded-[4px] outline outline-1 outline-dashed outline-[#3DDC84]/70 hover:outline-2 hover:bg-[#3DDC84]/10 transition-colors" data-testid={`edit-${k}`}>
      {get(k)}
    </span>
  );
}

export const EditBanner = () => (
  <div className="bg-[#3DDC84] text-[#13201A] text-sm font-semibold sticky top-0 z-50" data-testid="edit-mode-banner">
    <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1">
      <span className="inline-flex items-center gap-2"><Pencil size={15} /> Mode édition : cliquez sur un texte encadré pour le modifier.</span>
      <a href="/admin/rappels" className="ml-auto underline underline-offset-2" data-testid="edit-mode-back">Retour à l'admin</a>
    </div>
  </div>
);
